import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { useReferenceDataContext } from '../context/ReferenceDataContext';
import { findClosestTimezone } from '../utils/timezoneUtils';
import sellerAccountService from '../services/sellerAccountService';

interface AddSellerAccountCompProps {
    show: boolean;
    onClose: () => void;
    onAdded: () => void;
}

interface AddSellerAccountFormErrors {
    username?: string;
    password?: string;
    confirmPassword?: string;
    firstName?: string;
    lastName?: string;
    timezone?: string;
}

const AddSellerAccountComp: React.FC<AddSellerAccountCompProps> = ({ show, onClose, onAdded }) => {
    const modalRef = useRef<HTMLDivElement | null>(null);
    const bsModalRef = useRef<any>(null);

    const { seller } = useAppContext();
    const { timezones } = useReferenceDataContext();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [timezone, setTimezone] = useState('');

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formErrors, setFormErrors] = useState<AddSellerAccountFormErrors>({});
    const [serviceStatus, setServiceStatus] = useState({ type: '', message: '' });

    useEffect(() => {
        if (modalRef.current) {
            bsModalRef.current = new (window as any).bootstrap.Modal(modalRef.current);
            modalRef.current.addEventListener('hidden.bs.modal', onClose);
        }

        return () => {
            modalRef.current?.removeEventListener('hidden.bs.modal', onClose);
        };
    }, [onClose]);

    useEffect(() => {
        if (show) {
            resetForm();
            bsModalRef.current?.show();
        } else {
            bsModalRef.current?.hide();
        }
    }, [show]);

    useEffect(() => {
        if (show && timezones && timezones.length > 0 && !timezone) {
            const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const match = findClosestTimezone(timezones, browserTz);
            setTimezone(match?.timezoneId || '');
        }
    }, [show, timezones, timezone]);

    const resetForm = () => {
        setUsername('');
        setPassword('');
        setConfirmPassword('');
        setFirstName('');
        setLastName('');
        setTimezone('');
        setFormErrors({});
        setServiceStatus({ type: '', message: '' });
        setIsSubmitting(false);
    };

    const validateForm = () => {
        const errors: AddSellerAccountFormErrors = {};
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!username.trim()) {
            errors.username = 'Username is required';
        } else if (!emailPattern.test(username.trim())) {
            errors.username = 'Please enter a valid email address';
        }

        if (!password) {
            errors.password = 'Password is required';
        } else if (password.length < 6) {
            errors.password = 'Password must be at least 6 characters long';
        }

        if (!confirmPassword) {
            errors.confirmPassword = 'Please confirm the password';
        } else if (confirmPassword !== password) {
            errors.confirmPassword = 'Passwords do not match';
        }

        if (!firstName.trim()) {
            errors.firstName = 'First name is required';
        } else if (firstName.trim().length > 100) {
            errors.firstName = 'First name must be less than 100 characters';
        }

        if (!lastName.trim()) {
            errors.lastName = 'Last name is required';
        } else if (lastName.trim().length > 100) {
            errors.lastName = 'Last name must be less than 100 characters';
        }

        if (!timezone) {
            errors.timezone = 'Timezone is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setServiceStatus({ type: '', message: '' });

        if (!seller?.sellerId) {
            setServiceStatus({ type: 'Error', message: 'Seller information is missing.' });
            return;
        }

        if (!validateForm()) {
            return;
        }

        try {
            setIsSubmitting(true);

            const response = await sellerAccountService.addSellerAccount(
                username.trim(),
                password,
                firstName.trim(),
                lastName.trim(),
                timezone
            );

            if(response.result === 'Success') {
                bsModalRef.current?.hide();
                onAdded();
            }
            else if (response.result === 'UsernameExist') {
                setServiceStatus({ type: 'Error', message: 'This email already exists.' });
            }
            else if (response.result === 'PasswordTooShort') {
                setServiceStatus({ type: 'Error', message: 'Password must be at least 6 characters long.' });
            } 
            else if (response.result === 'IncompleteFields') {
                setServiceStatus({ type: 'Error', message: 'Please fill in all required fields.' });
            }
            else if(response.result === 'InvalidUsernameFormat') {
                setServiceStatus({ type: 'Error', message: 'Invalid username format. Please try again later.' });
            }
            else {
                setServiceStatus({ type: 'Error', message: 'Failed to create staff account.' });
            }

        } catch (error) {
            setServiceStatus({ type: 'Error', message: (error as Error).message || 'Failed to create staff account. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className="modal fade"
            tabIndex={-1}
            ref={modalRef}
            data-bs-backdrop="static"
            data-bs-keyboard="false"
        >
            <div className="modal-dialog">
                <div className="modal-content rounded-0">
                    <div className="modal-header">
                        <h5 className="modal-title">New Staff Account</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => bsModalRef.current?.hide()}
                        ></button>
                    </div>

                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row g-3">
                                <div className="col-md-12">
                                    <label htmlFor="addStaffUsername" className="form-label">Email</label>
                                    <input
                                        id="addStaffUsername"
                                        type="email"
                                        className={`form-control ${formErrors.username ? 'is-invalid' : ''}`}
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                    />
                                    {formErrors.username && (
                                        <div className="invalid-feedback">{formErrors.username}</div>
                                    )}
                                </div>

                                <div className="col-md-12">
                                    <label htmlFor="addStaffFirstName" className="form-label">First Name</label>
                                    <input
                                        id="addStaffFirstName"
                                        type="text"
                                        className={`form-control ${formErrors.firstName ? 'is-invalid' : ''}`}
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                    />
                                    {formErrors.firstName && (
                                        <div className="invalid-feedback">{formErrors.firstName}</div>
                                    )}
                                </div>

                                <div className="col-md-12">
                                    <label htmlFor="addStaffLastName" className="form-label">Last Name</label>
                                    <input
                                        id="addStaffLastName"
                                        type="text"
                                        className={`form-control ${formErrors.lastName ? 'is-invalid' : ''}`}
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                    />
                                    {formErrors.lastName && (
                                        <div className="invalid-feedback">{formErrors.lastName}</div>
                                    )}
                                </div>

                                <div className="col-md-12">
                                    <label htmlFor="addStaffPassword" className="form-label">Password</label>
                                    <input
                                        id="addStaffPassword"
                                        type="password"
                                        className={`form-control ${formErrors.password ? 'is-invalid' : ''}`}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                    {formErrors.password && (
                                        <div className="invalid-feedback">{formErrors.password}</div>
                                    )}
                                </div>

                                <div className="col-md-12">
                                    <label htmlFor="addStaffConfirmPassword" className="form-label">Confirm Password</label>
                                    <input
                                        id="addStaffConfirmPassword"
                                        type="password"
                                        className={`form-control ${formErrors.confirmPassword ? 'is-invalid' : ''}`}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                    />
                                    {formErrors.confirmPassword && (
                                        <div className="invalid-feedback">{formErrors.confirmPassword}</div>
                                    )}
                                </div>

                                {/* <div className="col-md-12">
                                    <label htmlFor="addStaffTimezone" className="form-label">Timezone</label>
                                    <select
                                        id="addStaffTimezone"
                                        className={`form-select ${formErrors.timezone ? 'is-invalid' : ''}`}
                                        value={timezone}
                                        onChange={(e) => setTimezone(e.target.value)}
                                    >
                                        <option value="">Select timezone</option>
                                        {timezones?.map((tz: any) => (
                                            <option key={tz.timezoneId} value={tz.timezoneId}>
                                                {tz.timezoneId}
                                            </option>
                                        ))}
                                    </select>
                                    {formErrors.timezone && (
                                        <div className="invalid-feedback">{formErrors.timezone}</div>
                                    )}
                                </div> */}

                                {serviceStatus.type === 'Error' && (
                                    <div className="col-md-12">
                                        <div className="alert alert-danger p-2 mb-0">
                                            {serviceStatus.message}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </form>
                    </div>

                    <div className="modal-footer d-flex justify-content-between">
                        <button
                            type="button"
                            className="btn btn-outline-dark rounded-0"
                            onClick={() => bsModalRef.current?.hide()}
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            className="btn btn-dark rounded-0"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>{' '}
                                    Saving ...
                                </>
                            ) : (
                                'Save'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddSellerAccountComp;